import { useMemo, useState, useCallback } from "react";

import {
  Box,
  Button,
  Center,
  Loader,
  ScrollArea,
  Space,
  Stack,
  Text,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  PlusCircle,
  SortDesc,
} from "~/../../base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "~/../../base-components/lib/theme/button";
import { useModalStack } from "~/../../shared-features/lib/features/modal";

import {
  CollectionSortDirection,
  CollectionSortableField,
  _CollectionType,
} from "apollo/graphql.generated";
import { TOOLS_URL } from "config";
import { useGetCollectionByUserPaginatedQuery } from "~/features/tokens/queries/collections.graphql.generated";

import SectionFilterResults from "../condition-modals/sections/SectionFilterResults";
import {
  HighlightCollections,
  pickSeriesImage,
} from "../condition-modals/tabs/CreatedOnHighlightTab";

import {
  SimpleInfiniteGrid,
  SimpleInfiniteListItem,
} from "./SimpleInfiniteGrid";

interface SortConfig {
  name: string;
  field: CollectionSortableField;
  direction: CollectionSortDirection;
}

const SORT_CONFIG: SortConfig[] = [
  {
    name: "Newest",
    field: CollectionSortableField.CREATED_DATE,
    direction: CollectionSortDirection.DESC,
  },
  {
    name: "Oldest",
    field: CollectionSortableField.CREATED_DATE,
    direction: CollectionSortDirection.ASC,
  },
  {
    name: "Alphabetical",
    field: CollectionSortableField.NAME,
    direction: CollectionSortDirection.ASC,
  },
];

export default function HighlightCollectionGrid(props: {
  selectedCollection?: SimpleInfiniteListItem<HighlightCollections[number]>;
  onSelectCollection: (
    collection: SimpleInfiniteListItem<HighlightCollections[number]>
  ) => void;
}) {
  const { selectedCollection, onSelectCollection } = props;
  const [searchQuery, setSearchQuery] = useState("");
  const [sort, setSort] = useState(SORT_CONFIG[0]);

  const {
    collections,
    loadMore,
    hasMore,
    loading: loadingCollections,
  } = usePaginatedHighlightCollections({
    filter: searchQuery,
    sortBy: sort.field,
    sortDirection: sort.direction,
  });

  const mappedCollections = useMemo<
    SimpleInfiniteListItem<HighlightCollections[number]>[]
  >(() => {
    if (collections == null) return [];

    return collections.map((collection) => {
      const image =
        (collection?.editions && collection.editions[0]?.image) ||
        (collection?.collectionType == _CollectionType.Series &&
          pickSeriesImage(collection)) ||
        collection?.importData?.media ||
        collection.generativeDetails?.logoUrl ||
        "";

      return {
        key: `${collection.contract?.address}-${collection.id}`,
        id: collection.id,
        title: collection.name,
        imageUrl: image,
        data: collection,
      };
    });
  }, [collections]);

  return (
    <Box>
      <SectionFilterResults
        placeholderText="Search by name"
        query={searchQuery}
        onChangeQuery={(value) => {
          console.log("filter", value);
          setSearchQuery(value);
        }}
        sortOptions={[
          {
            value: SORT_CONFIG[0].name,
            icon: <ArrowUp height={20} width={20} />,
          },
          {
            value: SORT_CONFIG[2].name,
            icon: <SortDesc height={20} width={20} />,
          },
          {
            value: SORT_CONFIG[1].name,
            icon: <ArrowDown height={20} width={20} />,
          },
        ]}
        sort={sort.name}
        onChangeSort={(s) =>
          setSort(SORT_CONFIG.find((sc) => sc.name === s) ?? SORT_CONFIG[0])
        }
        isLoading={loadingCollections}
      />

      <Space h={"xl"} />

      <ScrollArea.Autosize mih={300} maxHeight={500}>
        <SimpleInfiniteGrid<HighlightCollections[number]>
          items={mappedCollections}
          hasMore={hasMore}
          loading={loadingCollections}
          loadMore={loadMore}
          selectedItem={selectedCollection}
          onSelectItem={onSelectCollection}
          loadingElement={<LoadingElement />}
          emptyResultsElement={
            searchQuery === "" ? (
              <CreateNewCollection />
            ) : (
              <Stack align="center">
                <Text size={"lg"}>No Collections found.</Text>
              </Stack>
            )
          }
        />
      </ScrollArea.Autosize>
    </Box>
  );
}

function LoadingElement() {
  return (
    <Center h={300}>
      <Loader size={32} />
    </Center>
  );
}

function CreateNewCollection() {
  const navigate = useNavigate();
  const { popModal } = useModalStack();

  return (
    <>
      <Stack align="center">
        <Text size={"lg"}>You haven&apos;t created any collections yet.</Text>

        <Button
          leftIcon={<PlusCircle />}
          color={SECONDARY_COLOR}
          onClick={() => {
            popModal();
            navigate(TOOLS_URL.collections.create);
          }}
        >
          Create
        </Button>
      </Stack>
    </>
  );
}

function usePaginatedHighlightCollections(props?: {
  skip?: boolean;
  filter?: string;
  sortBy?: CollectionSortableField;
  sortDirection?: CollectionSortDirection;
}) {
  const { skip, filter, sortBy, sortDirection } = props ?? {};
  const [cursor, setCursor] = useState("");
  const pageSize = 12;

  const query = useGetCollectionByUserPaginatedQuery({
    variables: {
      first: pageSize,
      cursor: cursor || null,
      search: filter || null,
      sortBy: sortBy || null,
      sortDirection: sortDirection || null,
      contractId: null,
    },
    skip: skip,
    fetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: true,
    ...props,
  });

  const data = query.data?.getCollectionByUserPaginated;
  const nextCursor = data?.pageInfo.endCursor;

  const loadMore = useCallback(() => {
    query.fetchMore({
      variables: {
        cursor: nextCursor,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        fetchMoreResult.getCollectionByUserPaginated.collections = [
          ...(previousResult?.getCollectionByUserPaginated?.collections ?? []),
          ...(fetchMoreResult?.getCollectionByUserPaginated?.collections ?? []),
        ];
        return fetchMoreResult;
      },
    });
  }, [query.fetchMore, nextCursor]);

  const reset = () => {
    setCursor("");
  };

  return {
    ...query,
    collections: data?.collections || [],
    filter,
    hasMore: data?.pageInfo.hasNextPage,
    loadMore,
    reset,
  };
}
